/** Options modal: your display name, a link to your stats, difficulty / play-style preferences, lobby music, turn alerts, the rulebook pages, the Tutorial and Sign out. */
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { BOT_LEVELS, t, type BotLevel } from '@kadice/shared';
import { DICE_TYPES, TABLE_LAYOUTS, type DiceType, type Options, type TableLayout } from '../options';
import { canVibrate } from '../haptics';
import { canFullscreen, enterFullscreen, exitFullscreen } from '../fullscreen';
import { OptionRow, OptionTable } from './OptionTable';
import { disablePush, enablePush, pushState, type PushState } from '../turnAlerts';

/** Rulebook sections; each title is `options.book.<id>` in the text file. */
const BOOKS = {
  rules: { pages: [1, 2, 3, 4, 5, 6, 7].map((n) => `/img/rulebook/rules_${n}.jpg`) },
  characters: { pages: [1, 2, 3, 4, 5, 6].map((n) => `/img/rulebook/characters_${n}.jpg`) },
  sequence: { pages: ['/img/rulebook/sequence_of_play.jpg'] },
} as const;
type BookId = keyof typeof BOOKS;

export function OptionsMenu({
  options,
  onChange,
  onClose,
  nameEditor,
  avatarEditor,
  onSignOut,
  onStats,
  onTutorial,
}: {
  options: Options;
  onChange: (patch: Partial<Options>) => void;
  onClose: () => void;
  /** Your display name and its editor. */
  nameEditor: ReactNode;
  /** Your avatar: the defaults and an upload. */
  avatarEditor: ReactNode;
  /** Asks first. */
  onSignOut: () => void;
  /** Open your stat sheet (the leaderboard's My Stats). */
  onStats: () => void;
  /** Start the Tutorial (Learn to Play); absent in a game (the button is hidden). */
  onTutorial?: () => void;
}) {
  const [book, setBook] = useState<BookId | null>(null);
  if (book) return <BookModal key={book} book={book} onOpen={setBook} onClose={() => setBook(null)} />;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal options"
        role="dialog"
        aria-modal="true"
        aria-label={t('options.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row">
          <h3>{t('options.title')}</h3>
          <span className="spacer" />
          <button onClick={onClose}>{t('common.close')}</button>
        </div>

        <OptionTable>
          <OptionRow label={t('options.name')} hint={t('options.nameHint')}>
            {nameEditor}
          </OptionRow>
          <OptionRow label={t('options.avatar')} hint={t('options.avatarHint')}>
            {avatarEditor}
          </OptionRow>
          <OptionRow label={t('options.stats')} hint={t('options.statsHint')}>
            <button onClick={onStats}>{t('options.statsOpen')}</button>
          </OptionRow>
          <OptionRow label={t('options.highlight')} hint={t('options.highlightHint')} htmlFor="opt-highlight">
            <input
              id="opt-highlight"
              type="checkbox"
              checked={options.highlightAttackable}
              onChange={(e) => onChange({ highlightAttackable: e.target.checked })}
            />
          </OptionRow>
          <OptionRow label={t('options.beginner')} hint={t('options.beginnerHint')} htmlFor="opt-beginner">
            <input
              id="opt-beginner"
              type="checkbox"
              checked={options.beginnerMode}
              onChange={(e) => onChange({ beginnerMode: e.target.checked })}
            />
          </OptionRow>
          <OptionRow label={t('options.playStyle')} hint={t('options.playStyleHint')}>
            <label className="block">
              <input
                type="radio"
                name="playStyle"
                checked={options.playStyle === 'choose'}
                onChange={() => onChange({ playStyle: 'choose' })}
              />{' '}
              {t('options.choose')} <span className="muted small">{t('options.chooseHint')}</span>
            </label>
            <label className="block">
              <input
                type="radio"
                name="playStyle"
                checked={options.playStyle === 'random'}
                onChange={() => onChange({ playStyle: 'random' })}
              />{' '}
              {t('options.random')} <span className="muted small">{t('options.randomHint')}</span>
            </label>
          </OptionRow>
          <TurnAlertsOption />
          <OptionRow label={t('options.botLevel')} hint={t('options.botLevelHint')} htmlFor="opt-bot-level">
            <select
              id="opt-bot-level"
              value={options.botLevel}
              onChange={(e) => onChange({ botLevel: e.target.value as BotLevel })}
            >
              {BOT_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {t('options.botLevelOption', { level: t(`botLevel.${level}`), hint: t(`botLevel.${level}.hint`) })}
                </option>
              ))}
            </select>
          </OptionRow>
          <OptionRow label={t('options.music')} hint={t('options.musicHint')} htmlFor="opt-music">
            <input
              id="opt-music"
              type="checkbox"
              checked={options.music}
              onChange={(e) => onChange({ music: e.target.checked })}
            />
          </OptionRow>
          {options.music && (
            <OptionRow label={t('options.musicVolume')} htmlFor="opt-music-volume" className="suboption">
              <input
                id="opt-music-volume"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={options.musicVolume}
                onChange={(e) => onChange({ musicVolume: Number(e.target.value) })}
              />
            </OptionRow>
          )}
          <OptionRow label={t('options.layout')} hint={t('options.layoutHint')} htmlFor="opt-layout">
            <select
              id="opt-layout"
              value={options.layout}
              onChange={(e) => onChange({ layout: e.target.value as TableLayout })}
            >
              {TABLE_LAYOUTS.map((layout) => (
                <option key={layout} value={layout}>
                  {t(`options.layout.${layout}`)}
                </option>
              ))}
            </select>
          </OptionRow>
          <OptionRow label={t('options.diceType')} hint={t('options.diceTypeHint')} htmlFor="opt-dice-type">
            <select
              id="opt-dice-type"
              value={options.diceType}
              onChange={(e) => onChange({ diceType: e.target.value as DiceType })}
            >
              {DICE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`options.diceType.${type}`)}
                </option>
              ))}
            </select>
          </OptionRow>
          {options.diceType !== 'classic' && (
            <OptionRow label={t('options.enemy3d')} hint={t('options.enemy3dHint')} htmlFor="opt-enemy3d">
              <input
                id="opt-enemy3d"
                type="checkbox"
                checked={options.enemy3d}
                onChange={(e) => onChange({ enemy3d: e.target.checked })}
              />
            </OptionRow>
          )}
          <OptionRow label={t('options.fast')} hint={t('options.fastHint')} htmlFor="opt-fast">
            <input
              id="opt-fast"
              type="checkbox"
              checked={options.fastAnimations}
              onChange={(e) => onChange({ fastAnimations: e.target.checked })}
            />
          </OptionRow>
          <OptionRow label={t('options.autoNext')} hint={t('options.autoNextHint')} htmlFor="opt-auto-next">
            <input
              id="opt-auto-next"
              type="checkbox"
              checked={options.autoNext}
              onChange={(e) => onChange({ autoNext: e.target.checked })}
            />
          </OptionRow>
          <OptionRow label={t('options.fullscreen')} hint={t('options.fullscreenHint')} htmlFor="opt-fullscreen">
            <input
              id="opt-fullscreen"
              type="checkbox"
              checked={options.fullscreen && canFullscreen()}
              disabled={!canFullscreen()}
              title={canFullscreen() ? undefined : t('options.fullscreenUnsupported')}
              onChange={(e) => {
                // Straight from the tap: the browser only goes fullscreen on one.
                if (e.target.checked) enterFullscreen();
                else exitFullscreen();
                onChange({ fullscreen: e.target.checked });
              }}
            />
          </OptionRow>
          <OptionRow label={t('options.haptics')} hint={t('options.hapticsHint')} htmlFor="opt-haptics">
            <input
              id="opt-haptics"
              type="checkbox"
              checked={options.haptics && canVibrate()}
              disabled={!canVibrate()}
              title={canVibrate() ? undefined : t('options.hapticsUnsupported')}
              onChange={(e) => onChange({ haptics: e.target.checked })}
            />
          </OptionRow>
          <OptionRow label={t('options.debug')} hint={t('options.debugHint')} htmlFor="opt-debug">
            <input
              id="opt-debug"
              type="checkbox"
              checked={options.debug}
              onChange={(e) => onChange({ debug: e.target.checked })}
            />
          </OptionRow>
          {options.debug && (
            <OptionRow
              label={t('options.debugStats')}
              hint={t('options.debugStatsHint')}
              htmlFor="opt-debug-stats"
              className="suboption"
            >
              <input
                id="opt-debug-stats"
                type="checkbox"
                checked={options.debugStats}
                onChange={(e) => onChange({ debugStats: e.target.checked })}
              />
            </OptionRow>
          )}
          {options.debug && (
            <OptionRow
              label={t('options.botReasons')}
              hint={t('options.botReasonsHint')}
              htmlFor="opt-bot-reasons"
              className="suboption"
            >
              <input
                id="opt-bot-reasons"
                type="checkbox"
                checked={options.debugBotReasons}
                onChange={(e) => onChange({ debugBotReasons: e.target.checked })}
              />
            </OptionRow>
          )}
        </OptionTable>

        <div className="row actions book-buttons">
          {(Object.keys(BOOKS) as BookId[]).map((id) => (
            <Fragment key={id}>
              <button onClick={() => setBook(id)}>{t(`options.book.${id}`)}</button>
              {id === 'rules' && onTutorial && <button onClick={onTutorial}>{t('options.tutorial')}</button>}
            </Fragment>
          ))}
        </div>

        <div className="row sign-out">
          <span className="spacer" />
          <button className="danger" onClick={() => confirm(t('options.confirmSignOut')) && onSignOut()}>
            {t('header.signOut')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Why turn alerts can't be turned on here, by state. */
const ALERT_NOTES: Partial<Record<PushState, string>> = {
  unsupported: 'options.alertsUnsupported',
  serverOff: 'options.alertsOff',
  denied: 'options.alertsDenied',
  install: 'options.alertsInstall',
};

/** Turn alerts (Web Push) for this browser. Not a saved option: it's whether this browser is subscribed. */
function TurnAlertsOption() {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    void pushState().then(setState);
  }, []);
  if (state === null) return null;

  const toggle = async (on: boolean) => {
    setBusy(true);
    setFailed(false);
    try {
      if (on) setState(await enablePush());
      else {
        await disablePush();
        setState('off');
      }
    } catch {
      setFailed(true);
    }
    setBusy(false);
  };
  const note = ALERT_NOTES[state];
  return (
    <OptionRow label={t('options.alerts')} hint={t('options.alertsHint')} htmlFor="opt-alerts">
      <input
        id="opt-alerts"
        type="checkbox"
        checked={state === 'on'}
        disabled={busy || (state !== 'on' && state !== 'off')}
        onChange={(e) => void toggle(e.target.checked)}
      />
      {note && <span className="small block">{t(note)}</span>}
      {failed && <span className="error small block">{t('options.alertsFailed')}</span>}
    </OptionRow>
  );
}

/** Pages of a rulebook section, read like a book: Back / Next, or ← / → keys. Buttons jump to the other sections. */
function BookModal({ book, onOpen, onClose }: { book: BookId; onOpen: (id: BookId) => void; onClose: () => void }) {
  const { pages } = BOOKS[book];
  const title = t(`options.book.${book}`);
  const [page, setPage] = useState(0);
  const last = pages.length - 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setPage((p) => Math.min(p + 1, last));
      else if (e.key === 'ArrowLeft') setPage((p) => Math.max(p - 1, 0));
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [last, onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal book"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row">
          <h3>{title}</h3>
          <span className="spacer" />
          <button onClick={onClose}>{t('common.close')}</button>
        </div>
        <div className="row book-buttons">
          {(Object.keys(BOOKS) as BookId[])
            .filter((id) => id !== book)
            .map((id) => (
              <button key={id} onClick={() => onOpen(id)}>
                {t(`options.book.${id}`)}
              </button>
            ))}
        </div>
        <img src={pages[page]} alt={t('book.pageAlt', { title, page: page + 1 })} className="book-page" />
        {pages.length > 1 && (
          <div className="row book-nav">
            <button disabled={page === 0} onClick={() => setPage(page - 1)}>
              {t('book.back')}
            </button>
            <span className="muted">{t('book.page', { page: page + 1, pages: pages.length })}</span>
            <button disabled={page === last} onClick={() => setPage(page + 1)}>
              {t('book.next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
